import React, { useState, useEffect } from 'react';
import {
    getFaqCategories,
    createFaqCategory,
    updateFaqCategory,
    deleteFaqCategory,
    FaqCategory
} from '../api/admin';
import styles from '../pages/CommonPage.module.css';
import { Plus, Edit, Trash2, X } from 'lucide-react';

interface Props {
    onClose: () => void;
}

export default function FaqCategoryManager({ onClose }: Props) {
    const [categories, setCategories] = useState<FaqCategory[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({ name: '', displayOrder: 0 });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const data = await getFaqCategories();
            setCategories(data);
        } catch (error) {
            console.error('Failed to fetch categories', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingId) {
                await updateFaqCategory(editingId, formData);
            } else {
                await createFaqCategory(formData);
            }
            setEditingId(null);
            setFormData({ name: '', displayOrder: 0 });
            fetchCategories();
        } catch (error) {
            alert('저장 실패: ' + error);
        }
    };

    const handleEdit = (cat: FaqCategory) => {
        setEditingId(cat.id);
        setFormData({ name: cat.name, displayOrder: cat.displayOrder });
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('삭제하시겠습니까? 연결된 FAQ가 있다면 오류가 발생할 수 있습니다.')) return;
        try {
            await deleteFaqCategory(id);
            fetchCategories();
        } catch (error) {
            alert('삭제 실패. 연결된 FAQ가 있는지 확인해주세요.');
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal} style={{ maxWidth: '600px' }}>
                <div className={styles.modalHeader}>
                    <h2>카테고리 관리</h2>
                    <button 
                        className={styles.closeBtn} 
                        onClick={onClose}
                        aria-label="닫기"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            transition: 'all 0.2s'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.formCard} style={{ padding: '20px', background: '#f9fafb', marginBottom: '20px' }}>
                    <form onSubmit={handleSubmit}>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 120px', 
                            gap: '16px',
                            marginBottom: '16px'
                        }}>
                            <div>
                                <label style={{ 
                                    fontSize: '0.875rem', 
                                    display: 'block', 
                                    marginBottom: '8px',
                                    fontWeight: 500,
                                    color: '#374151'
                                }}>
                                    카테고리명
                                </label>
                                <input
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="카테고리 이름을 입력하세요"
                                    style={{ 
                                        padding: '10px 12px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #d1d5db', 
                                        width: '100%',
                                        fontSize: '0.875rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ 
                                    fontSize: '0.875rem', 
                                    display: 'block', 
                                    marginBottom: '8px',
                                    fontWeight: 500,
                                    color: '#374151'
                                }}>
                                    순서
                                </label>
                                <input
                                    type="number"
                                    required
                                    min="0"
                                    value={formData.displayOrder}
                                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                                    style={{ 
                                        padding: '10px 12px', 
                                        borderRadius: '6px', 
                                        border: '1px solid #d1d5db', 
                                        width: '100%',
                                        fontSize: '0.875rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            gap: '12px', 
                            justifyContent: 'flex-end',
                            paddingTop: '4px'
                        }}>
                            {editingId && (
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={() => {
                                        setEditingId(null);
                                        setFormData({ name: '', displayOrder: 0 });
                                    }}
                                >
                                    취소
                                </button>
                            )}
                            <button type="submit" className={styles.primaryBtn}>
                                {editingId ? '수정' : '추가'}
                            </button>
                        </div>
                    </form>
                </div>

                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>순서</th>
                            <th>이름</th>
                            <th style={{ textAlign: 'center' }}>관리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map((cat) => (
                            <tr key={cat.id}>
                                <td>{cat.displayOrder}</td>
                                <td>{cat.name}</td>
                                <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                        <button onClick={() => handleEdit(cat)} className={styles.iconBtn}>
                                            <Edit size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(cat.id)} className={styles.iconBtn} style={{ color: '#ef4444' }}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
